import {Button, Card, Checkbox, Flex, Input, Popover, Space} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUpWideShort, faPencil, faRotate} from "@fortawesome/free-solid-svg-icons";
import React, {useEffect} from "react";
import TextArea from "antd/es/input/TextArea";
import {useParams} from "react-router-dom";
import {categories} from "./list/SettingList";
import {mergeOverlappedStrings} from "./SettingElementDetails";
import {generateLoreItemPrompt, improveLoreItemPrompt} from "./prompts/ItemsPrompts";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {DraftComponent, DraftComponentCategory, DraftElement, DraftElementItem} from "../../../../../../feature/Draft";
import {aiChainPrompt, getEmbedding, similaritySearch, unembed} from "../../../../../../ai/Prompt";
import {updateLoreItem} from "../../../../../../feature/project/draftSlice";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";
import {EmbedButton} from "../../../../../../ai/components/EmbedButton";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import {EmbeddingErrorMessage} from "../../../../../../ai/components/EmbeddingErrorMessage";

export const SettingItemDetails = (
    props: {
        category: DraftComponentCategory,
        component: DraftComponent,
        element: DraftElement,
        data: DraftElementItem,
    }
) => {
    const draft = useAppSelector(state => state.draftSlice.draft)
    const model = useAppSelector(state => state.draftSlice.model)
    const {projectId} = useParams()
    const [loading, setLoading] = React.useState(false)
    const [open, setOpen] = React.useState(false)
    const [useContext, setUseContext] = React.useState(true)
    const [document, setDocument] = React.useState<string[] | undefined>(undefined)
    const [showWarning, setShowWarning] = React.useState(false)
    const [unembedding, setUnembedding] = React.useState(false)
    const [prompt, setPrompt] = React.useState<string>(categories.find(c => c.key == props.category.category)?.prompt.lore.extend + '\n\nЛор: ' + props.element.title)
    const dispatch = useAppDispatch()
    const aiProviders = useAppSelector(state => state.aiSlice.projectAiProviderSettings)

    const getEmbeddings = () => {
        if (aiProviders.map(p => p.name).includes('OpenAI')) {

            getEmbedding({
                project: projectId,
                ids: [props.category.category + '-component' + props.component.id + '-element' + props.element.id + '-item' + props.data.id]
            })
                .then((data) => {
                    setDocument(data.documents)
                })
        }
    }

    useEffect(() => {
        getEmbeddings()
    }, [props.data.id]);

    useEffect(() => {
        const doc = mergeOverlappedStrings(document ?? [])?.replaceAll("\n", "").replaceAll(" ", "")
        const desc = props.data.description?.split("\n\n").join(" ")?.replaceAll("\n", "").replaceAll(" ", "")

        dispatch(updateLoreItem({
            category: props.category.category,
            componentId: props.component.id,
            elementId: props.element.id,
            item: {
                ...props.data,
                embedded: (!doc || doc.trim().length == 0) ? false : doc == desc ? true : undefined
            }
        }))
    }, [document]);

    useEffect(() => {
        setPrompt(categories.find(c => c.key == props.category.category)?.prompt.lore.extend + '\n\nЭлемент: ' + props.data.title)
    }, [props.data]);

    const generateItem = async () => {
        const category = categories.find(c => c.key == props.category.category)!
        let context = await similaritySearch({
            project: projectId,
            query: props.data.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: generateLoreItemPrompt({
                genres: draft.properties.genre,
                customPrompt: category.prompt.lore.single + '\n\n"Элемент": ' + props.data.title,
                synopsis: draft.properties.synopsis,
                element: props.element,
                setting: draft.setting.description,
                context: (props.data.description ? [props.data.description] : []).concat(context.documents).flat()
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateLoreItem({
            category: props.category.category,
            componentId: props.component.id,
            elementId: props.element.id,
            item: {
                ...props.data,
                description: data.data.description,
            }
        }))
    }

    const improveItem = async () => {
        const category = categories.find(c => c.key == props.category.category)!
        let context = useContext ? await similaritySearch({
            project: projectId,
            query: (props.data.description ?? '') + '\n' + prompt,
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}]}
        }) : undefined
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: improveLoreItemPrompt({
                genres: draft.properties.genre,
                customPrompt: prompt ?? (category.prompt.lore.extend + '\n\nЭлемент: ' + props.data.title),
                synopsis: draft.properties.synopsis,
                element: props.element,
                item: props.data,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateLoreItem({
            category: props.category.category,
            componentId: props.component.id,
            elementId: props.element.id,
            item: {
                ...props.data,
                description: data.data.description,
            }
        }))
    }

    return (
        <>
            <Card styles={{body: {height: 'calc(100% - 72px)'}}} style={{height: '100%'}} title={
                <Flex gap={10}>
                    <DebounceInput
                        style={{fontWeight: 'bold', background: "#fff"}}
                        placeholder={"Component title"}
                        value={props.data.title}
                        disabled={props.data.embedded}
                        onChange={(text) => {
                            dispatch(updateLoreItem({
                                category: props.category.category ?? '',
                                componentId: props.data.id,
                                elementId: props.element.id,
                                item: {
                                    ...props.data,
                                    title: text
                                }
                            }))
                        }}/>

                    <Popover
                        placement={'bottomRight'}
                        content={
                            <Space direction={'vertical'}>
                                <TextArea onChange={(e) => setPrompt(e.target.value)}
                                          value={prompt}
                                          autoSize={true}
                                          style={{width: 300}}
                                />
                                <Checkbox checked={useContext} onChange={(e) => {
                                    setUseContext(e.target.checked)
                                }}>Include context</Checkbox>
                                <Button
                                    type={'primary'}
                                    onClick={async () => {
                                        if (props.data.embedded) {
                                            setShowWarning(true)
                                        } else {
                                            setLoading(true)
                                            setOpen(false)
                                            await improveItem()
                                            setLoading(false)
                                        }
                                    }}>Go</Button>
                            </Space>
                        }
                        title="Prompt"
                        trigger="click"
                        open={open}
                        onOpenChange={(e) => setOpen(e)}
                    >
                        <GenButton
                            mode={'icon'}
                            hideMainAction={true}
                            loading={loading}
                            icon={<IconBoltGrad/>}
                            open={!open}
                            actions={[{
                                key: "1",
                                title: "Re-generate",
                                action: async (model: string) => {
                                    if (props.data.embedded) {
                                        setShowWarning(true)
                                    } else {
                                        await generateItem()
                                            .catch((e) => {
                                                console.log(e)
                                            })
                                    }
                                },
                                render: (key, title, action) => {
                                    return <Space><BoltIcon secondary={faRotate} size={16}/>{title}</Space>
                                }
                            },
                                {
                                    key: "2", title: "Improve", action: async (model: string) => {
                                        if (props.data.embedded) {
                                            setShowWarning(true)
                                        } else {
                                            await improveItem()
                                        }
                                    }, render: (key, title, action) => {
                                        return <Flex gap={10} justify={'space-between'}>
                                            <BoltIcon secondary={faArrowUpWideShort} size={16}/>{title}
                                            <Button
                                                type={'text'} size={'small'}
                                                icon={<FontAwesomeIcon icon={faPencil}/>}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setOpen(true)
                                                }}
                                            />
                                        </Flex>
                                    }
                                }]
                            }/>

                    </Popover>

                    {props.data.description && props.data.description?.length > 0 ?
                        <EmbedButton
                            data={{
                                id: props.category.category + '-component' + props.component.id + '-element' + props.element.id + '-item' + props.data.id,
                                text: props.data.description ?? '',
                                metadata:
                                    {
                                        title: props.data.title ?? '',
                                        category: props.category.category ?? '',
                                        type: 'setting'
                                    },

                            }}
                            chunkSize={800}
                            chunkOverlap={100}
                            embedded={props.data.embedded}
                            edited={false}
                            onEmbedded={(data) => {
                                dispatch(updateLoreItem({
                                    category: props.category.category,
                                    componentId: props.component.id,
                                    elementId: props.element.id,
                                    item: {
                                        ...props.data,
                                        embedded: true
                                    }
                                }))
                                getEmbeddings()
                            }}
                            onUnembedded={(data) => {
                                dispatch(updateLoreItem({
                                    category: props.category.category,
                                    componentId: props.component.id,
                                    elementId: props.element.id,
                                    item: {
                                        ...props.data,
                                        embedded: false
                                    }
                                }))
                                getEmbeddings()
                            }}
                        />
                        : ''}
                </Flex>
            }
            >
                <TextAreaActions
                    style={{border: "none", height: '100%'}}
                    value={props.data.description}
                    onActionSelected={() => {
                        if (props.data.embedded) {
                            setShowWarning(true)
                            return false
                        } else {
                            return true
                        }
                    }}
                    validate={text => {
                        if (props.data.embedded) {
                            setShowWarning(true)
                            return false
                        } else {
                            return true
                        }
                    }}
                    onChange={(text: string) => {
                        dispatch(updateLoreItem({
                            category: props.category.category,
                            componentId: props.component.id,
                            elementId: props.element.id,
                            item: {
                                ...props.data,
                                description: text
                            }
                        }))

                    }}
                />
            </Card>

            {showWarning ? <EmbeddingErrorMessage
                onClose={() => setShowWarning(false)}
                content={<>
                    <div>You are editing an embedded element:</div>
                    <ul>
                        <li style={{fontWeight: '700'}}>{props.data.title}</li>
                    </ul>
                    <div>You have to un-embed it before editing</div>
                </>}
                loading={unembedding}
                onUnmbedClick={() => {
                    setUnembedding(true)
                    unembed({
                        project: projectId,
                        ids: [props.category.category + '-component' + props.component.id + '-element' + props.element.id + '-item' + props.data.id]
                    })
                        .then(() => {
                            dispatch(updateLoreItem({
                                category: props.category.category,
                                componentId: props.component.id,
                                elementId: props.element.id,
                                item: {
                                    ...props.data,
                                    embedded: false
                                }
                            }))
                        }).finally(() => {
                        setUnembedding(false)
                        setShowWarning(false)
                        getEmbeddings()
                    })
                }}
            /> : ''}
        </>
    )
}